import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { memo, useCallback, useEffect, useState } from 'react';
import { MapMouseEvent, Map as MapboxMap, ViewStateChangeEvent, useMap } from 'react-map-gl/mapbox';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import {
  ALARMS_REPORTS_PAGE,
  CONTROL_TOWER_PAGE,
  CONVOYS_PAGE,
  DELIVERY_GEOFENCES_PAGE,
  DOOR_REPORTS_PAGE,
  EVIDENCE_REPORT_DETAIL_PAGE,
  GEOFENCES_PAGE,
  GPT_DETAIL_PAGE,
  JOURNEYS_PAGE,
  LINKS_PAGE,
  LIVE_STATUS_PAGE,
  NOTIFICATION_EVIDENCES_PAGE,
  NOTIFICATION_EVIDENCE_DETAIL_PAGE,
  NOTIFICATION_PAGE,
  PATHS_REPORTS_PAGE,
  PLACES_PAGE,
  REQUEST_VIDEO_PAGE,
  ROUTE_REPORTS_PAGE,
  SECONDARIES_PAGE,
  TELEMETRY_REPORTS_PAGE,
  TRACKING_PAGE,
} from '../../constants/app';
import { JOURNEY_END_POINT, JOURNEY_START_POINT } from '../../constants/journeys';
import { environment } from '../../environments/environment';
import { useDebounceFunction } from '../../hooks/useDebounce';
import { getMapCurrentStates } from '../../services/mapService';
import { accountState } from '../../storage/account';
import {
  isAllWorkspacesSelect,
  pageState,
  showClustersState,
  sidenavIsPinnedState,
  sidenavIsVisibleState,
  workspaceState,
} from '../../storage/app';
import { showDeliveryFenceControlsState } from '../../storage/deliveryFences';
import { evidenceMediaState } from '../../storage/evidences';
import { selectedFenceIdState } from '../../storage/fences';
import { editingJourneyState, journeySelectPointState, selectedJourneyIdState } from '../../storage/journeys';
import {
  bottomMapPaddingState,
  interactiveLayersState,
  leftMapPaddingState,
  mapCurrentStatesState,
  mapDrawModeState,
  mapNewCircleState,
  mapStyleState,
  mapViewState,
  navCtrl,
  rightMapPaddingState,
  topMapPaddingState,
} from '../../storage/map';
import { editSelectedPlaceState, editingPlaceState } from '../../storage/places';
import { activeReportAlarmState, activeReportRouteState, activeTelemetryState } from '../../storage/reports';
import { MapPointerInfo } from '../../types/app';
import { getAddressFromPoint } from '../../utils/geocode';
import PathMarkers from '../reports/pathsReport/PathMarkers';
import { MapOptions } from './MapOptions';
import { MapSearchBox } from './MapSearchBox';
import { PointerInfo } from './PointerInfo';
import DeliveryFenceDrawControls from './draw/DeliveryFenceDrawControls';
import FenceDrawControls from './draw/FenceDrawControls';
import CapturingTimelinePathLayer from './layers/CapturingTimelinePathLayer';
import CapturingVideoPathsLayer from './layers/CapturingVideoPathsLayer';
import { DeliveryFenceLayer } from './layers/DeliveryFenceLayer';
import { DeliveryFencesLayer } from './layers/DeliveryFencesLayer';
import { FencesLayer } from './layers/FencesLayer';
import NotificationLayer from './layers/NotificationLayer';
import PathLayer from './layers/PathLayer';
import { PlacesLayer } from './layers/PlacesLayer';
import RouteLayer from './layers/RouteLayer';
import SpotsLayer from './layers/SpotsLayer';
import TelemetryLayer from './layers/TelemetryLayer';
import TrackingLayer from './layers/TrackingLayer';
import { TrafficLayer } from './layers/TrafficLayer';
import { ZoneFencesLayer } from './layers/ZoneFencesLayer';
import CapturingTimelineMarker from './markers/CapturingTimelineMarker';
import CapturingVideoEventMarker from './markers/CapturingVideoEventMarker';
import CapturingVideoMarker from './markers/CapturingVideoMarker';
import CapturingVideoPathsMarkers from './markers/CapturingVideoPathsMarkers';
import Clusters from './markers/Clusters';
import DoorStateMarker from './markers/DoorStateMarkers';
import EvaluationMarkers from './markers/EvaluationMarkers';
import EvidenceMediaMarker from './markers/EvidenceMediaMarker';
import GptEventAlarmCSMarker from './markers/GptEventAlarmCSMarker';
import GptEventAlarmsMarkers from './markers/GptEventAlarmsMarkers';
import GptEventsClusters from './markers/GptEventsClusters';
import JourneyMarkers from './markers/JourneyMarkers';
import LinkMarkers from './markers/LinkMarkers';
import LiveStatusClusters from './markers/LiveStatusClusters';
import MultipleDeviceMarkers from './markers/MultipleDeviceMarkers';
import NotificationMarker from './markers/NotificationMarker';
import PlaceMarker from './markers/PlaceMarker';
import ReportAlarmsMarkers from './markers/ReportAlarmsMarkers';
import RouteMarkers from './markers/RouteMarkers';
import TelemetryMarkers from './markers/TelemetryMarkers';
import TempPositionMarker from './markers/TempPositionMarker';
import TrackingMarkers from './markers/TrackingMarkers';
import './styles/mapbox.css';

function Map() {
  const { map } = useMap();
  const [viewState, setViewState] = useRecoilState(mapViewState);
  const [pointerInfo, setPointerInfo] = useState<MapPointerInfo>();
  const mapStyle = useRecoilValue(mapStyleState);
  const sidenavIsPinned = useRecoilValue(sidenavIsPinnedState);
  const sidenavIsVisible = useRecoilValue(sidenavIsVisibleState);
  const interactiveLayers = useRecoilValue(interactiveLayersState);
  const leftPadding = useRecoilValue(leftMapPaddingState);
  const rightPadding = useRecoilValue(rightMapPaddingState);
  const bottomPadding = useRecoilValue(bottomMapPaddingState);
  const topPadding = useRecoilValue(topMapPaddingState);
  const account = useRecoilValue(accountState);
  const workspace = useRecoilValue(workspaceState);
  const setMapCurrentStates = useSetRecoilState(mapCurrentStatesState);
  const page = useRecoilValue(pageState);
  const drawMode = useRecoilValue(mapDrawModeState);
  const setNewCircle = useSetRecoilState(mapNewCircleState);
  const selectedJourneyId = useRecoilValue(selectedJourneyIdState);
  const journeySelectPoint = useRecoilValue(journeySelectPointState);
  const resetJourneySelectPoint = useResetRecoilState(journeySelectPointState);
  const setEditingJourney = useSetRecoilState(editingJourneyState);
  const [editingPlace, setEditingPlace] = useRecoilState(editingPlaceState);
  const isEditingPlace = useRecoilValue(editSelectedPlaceState);
  const hasActiveRoute = !!useRecoilValue(activeReportRouteState);
  const validRoute = page === ROUTE_REPORTS_PAGE.id && hasActiveRoute;
  const hasActiveTelemetry = !!useRecoilValue(activeTelemetryState);
  const validTelemetry = page === TELEMETRY_REPORTS_PAGE.id && hasActiveTelemetry;
  const hasActiveReportAlarm = !!useRecoilValue(activeReportAlarmState);
  const validReportAlarm = page === ALARMS_REPORTS_PAGE.id && hasActiveReportAlarm;
  const showClusters = useRecoilValue(showClustersState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const selectedMedia = useRecoilValue(evidenceMediaState);
  const [activeNavCtrl, setActiveNavCtrl] = useRecoilState(navCtrl);
  const showFencesControls = useRecoilValue(selectedFenceIdState) !== undefined && page === GEOFENCES_PAGE.id;
  const showDeliveryFencesControls = useRecoilValue(showDeliveryFenceControlsState);
  // Time needed for side menu animation
  const mapResizeDelayed = useDebounceFunction(function () {
    map?.resize();
  }, 350);

  const mapResize = useDebounceFunction(function () {
    map?.resize();
  }, 0);

  useEffect(() => {
    if (map) {
      queueMicrotask(() => {
        if (!sidenavIsPinned || !sidenavIsVisible) {
          mapResizeDelayed();
          return;
        }
        mapResize();
      });
    }
  }, [map, sidenavIsPinned, sidenavIsVisible]);

  useEffect(() => {
    queueMicrotask(() => {
      map?.resize();
    });
    if (map && !activeNavCtrl) {
      const nav = new mapboxgl.NavigationControl({ showCompass: false });
      map?.addControl(nav, 'bottom-right');
      setActiveNavCtrl(!activeNavCtrl);
    }
  }, [map]);

  useEffect(() => {
    if (!account?.client || !workspace?._id || !map) return;
    updateMapCurrentStates();
  }, [account?.client, workspace?._id, map, leftPadding, rightPadding, bottomPadding, topPadding]);

  const updateMapCurrentStates = async () => {
    if (!account?.client || !workspace?._id || !map) return;
    const bounds = map.getBounds();
    const northWest = bounds?.getNorthWest();
    const southEast = bounds?.getSouthEast();
    if (northWest && southEast) {
      const { currentStates, error } = await getMapCurrentStates(
        northWest.lng,
        northWest.lat,
        southEast.lng,
        southEast.lat,
        account.client._id,
        isAllWorkspacesSelected ? undefined : workspace._id,
      );
      if (!error && currentStates) {
        setMapCurrentStates(currentStates);
      } else {
        setMapCurrentStates(undefined);
      }
    }
  };

  const onMoveEnd = (ev: ViewStateChangeEvent) => {
    setViewState(ev.viewState);
    updateMapCurrentStates();
  };

  const onMouseMove = useCallback((event: MapMouseEvent) => {
    const {
      features,
      point: { x, y },
    } = event;
    let properties = {};
    if (features?.length) {
      const allProps = features.map((feature) => feature.properties);
      properties = Object.assign({}, ...allProps);
    }
    setPointerInfo(features && features[0] && { properties, x, y });
  }, []);

  const onClick = async (ev: MapMouseEvent) => {
    const point = {
      lat: ev.lngLat.lat,
      lng: ev.lngLat.lng,
    };
    if ((page === GEOFENCES_PAGE.id || page === DELIVERY_GEOFENCES_PAGE.id) && drawMode === 'circle') {
      setNewCircle({
        ...point,
        radius: 100,
      });
    }
    if (page === JOURNEYS_PAGE.id && journeySelectPoint) {
      const address = await getAddressFromPoint(point);
      if (journeySelectPoint === JOURNEY_START_POINT) {
        setEditingJourney((prev) => ({
          ...prev!,
          startPoint: point,
          originAddress: address,
        }));
      } else if (journeySelectPoint === JOURNEY_END_POINT) {
        setEditingJourney((prev) => ({
          ...prev!,
          endPoint: point,
          destinationAddress: address,
        }));
      }
      resetJourneySelectPoint();
    }
    if (page === PLACES_PAGE.id && editingPlace && isEditingPlace) {
      const address = await getAddressFromPoint(point);
      setEditingPlace((prev) => ({
        ...prev!,
        address: address ?? '',
        geoPoint: {
          type: 'Point',
          coordinates: [point.lng, point.lat],
        },
      }));
    }
  };

  return (
    <MapboxMap
      id="map"
      reuseMaps
      mapboxAccessToken={environment.mapboxAPIKey}
      initialViewState={{ ...viewState }}
      onClick={onClick}
      mapStyle={mapStyle}
      onMoveEnd={onMoveEnd}
      onMouseMove={onMouseMove}
      interactiveLayerIds={interactiveLayers}
      projection={{ name: 'mercator' }}
      padding={{
        left: leftPadding,
        bottom: bottomPadding,
        right: rightPadding,
        top: topPadding,
      }}
      style={{ position: 'absolute' }}
      scrollZoom={true}
      keyboard={true}
      doubleClickZoom={true}
      dragRotate={false}
      minZoom={0}
      maxZoom={18}
    >
      <PointerInfo info={pointerInfo} />
      <TrafficLayer />
      <SpotsLayer />
      <ZoneFencesLayer />
      <FencesLayer />
      <DeliveryFencesLayer />
      <DeliveryFenceLayer />
      {showClusters && <Clusters />}
      <MapSearchBox />
      <MapOptions />
      {showFencesControls && <FenceDrawControls />}
      {showDeliveryFencesControls && <DeliveryFenceDrawControls />}
      {page === JOURNEYS_PAGE.id && selectedJourneyId !== undefined && <JourneyMarkers />}
      {page === TRACKING_PAGE.id && (
        <>
          <TrackingLayer />
          <TrackingMarkers />
        </>
      )}
      {page === SECONDARIES_PAGE.id && <MultipleDeviceMarkers />}
      {(page === SECONDARIES_PAGE.id || page === LINKS_PAGE.id || page === CONVOYS_PAGE.id) && <EvaluationMarkers />}
      {page === LINKS_PAGE.id && <LinkMarkers />}
      {validRoute && (
        <>
          <RouteLayer />
          <RouteMarkers />
        </>
      )}
      {validTelemetry && (
        <>
          <TelemetryLayer />
          <TelemetryMarkers />
        </>
      )}
      {validReportAlarm && <ReportAlarmsMarkers />}
      {page === DOOR_REPORTS_PAGE.id && <DoorStateMarker />}
      {page === PLACES_PAGE.id && (
        <>
          <PlacesLayer />
          <PlaceMarker />
        </>
      )}
      {page === PATHS_REPORTS_PAGE.id && (
        <>
          <PathLayer />
          <PathMarkers />
        </>
      )}
      {(page === EVIDENCE_REPORT_DETAIL_PAGE.id || page === NOTIFICATION_EVIDENCE_DETAIL_PAGE.id) && selectedMedia && (
        <EvidenceMediaMarker media={selectedMedia} />
      )}
      {page === NOTIFICATION_PAGE.id && (
        <>
          <NotificationLayer />
          <NotificationMarker />
        </>
      )}
      {page === NOTIFICATION_EVIDENCES_PAGE.id && <NotificationMarker />}
      {page === LIVE_STATUS_PAGE.id && <LiveStatusClusters />}
      {page === GPT_DETAIL_PAGE.id && (
        <>
          <GptEventAlarmsMarkers />
          <GptEventAlarmCSMarker />
        </>
      )}
      {page === CONTROL_TOWER_PAGE.id && <GptEventsClusters />}
      {/* FIXME: better validation based on pages / actions */}
      {page === REQUEST_VIDEO_PAGE.id && (
        <>
          <CapturingVideoPathsLayer />
          <CapturingTimelinePathLayer />
          <CapturingVideoEventMarker />
          <CapturingTimelineMarker />
          <CapturingVideoPathsMarkers />
          <CapturingVideoMarker />
        </>
      )}
      <TempPositionMarker />
    </MapboxMap>
  );
}

export default memo(Map);
